import { useTranslation } from 'react-i18next';
import { SaleOffer } from '../../api/SalesAPI';
import InputShortText from '../InputShortText/InputShortText';
import SelectInput from '../SelectInput/SelectInput';
import { Icon } from '../Icon/Icon';
import { applyOffer, isOfferPresent } from '../../utils/saleLineItems';
import deleteIcon from '../../assets/images/bin.svg';

type Props = {
  offer?: SaleOffer;
  baseAmount: number;
  currency?: string;
  onChange: (offer: SaleOffer | undefined) => void;
  isDisabled?: boolean;
  disabledHint?: string;
  compact?: boolean;
  hideTitle?: boolean;
  hideClearButton?: boolean;
  /** Renders a bin icon in the top-right corner of the discount box (e.g. to discard a draft). */
  onClear?: () => void;
};

function SaleOfferFields({
  offer,
  baseAmount,
  currency = 'EUR',
  onChange,
  isDisabled = false,
  disabledHint,
  compact = false,
  hideTitle = false,
  hideClearButton = false,
  onClear,
}: Props) {
  const { t } = useTranslation();

  const updateOffer = (patch: Partial<SaleOffer>) => {
    const next: SaleOffer = {
      concept: patch.concept ?? offer?.concept ?? '',
      discountType: patch.discountType ?? offer?.discountType ?? 'PERCENT',
      discountValue: patch.discountValue ?? offer?.discountValue ?? 0,
    };
    if (!next.concept.trim()) {
      onChange(undefined);
      return;
    }
    onChange(next);
  };

  const handleClear = () => onChange(undefined);

  const lineTotal = isOfferPresent(offer) ? applyOffer(baseAmount, offer) : baseAmount;

  return (
    <div className={`relative rounded-md border border-dashed border-gray-200 bg-gray-50/80 ${compact ? 'p-2 space-y-2' : 'p-3 space-y-3'}`}>
      {onClear && !isDisabled && (
        <button
          type="button"
          onClick={onClear}
          aria-label={t('sales.form.offer.clear') || 'Clear offer'}
          className="absolute right-1.5 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
        >
          <Icon url={deleteIcon} cssStyles="w-4" />
        </button>
      )}
      {!hideTitle && (
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-700">
          {t('sales.form.offer.title') || 'Offer'}
        </p>
        {!hideClearButton && !isDisabled && isOfferPresent(offer) && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            aria-label={t('sales.form.offer.clear') || 'Clear offer'}
          >
            <Icon url={deleteIcon} cssStyles="w-3.5" />
            {t('sales.form.offer.clear') || 'Clear'}
          </button>
        )}
      </div>
      )}
      {disabledHint && (
        <p className="text-xs text-amber-700">{disabledHint}</p>
      )}
      <InputShortText
        label={t('sales.form.offer.concept') || 'Concept'}
        defaultValue={offer?.concept ?? ''}
        onChange={(e) => updateOffer({ concept: e.target.value })}
        isDisabled={isDisabled}
        placeholder={t('sales.form.offer.concept_placeholder') || 'e.g. No VAT day'}
        maxLength={255}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <SelectInput
          label={t('sales.form.offer.discount_type') || 'Discount type'}
          defaultValue={offer?.discountType ?? 'PERCENT'}
          values={[
            { value: 'PERCENT', readableValue: t('sales.form.offer.type_percent') || 'Percentage (%)' },
            { value: 'FIXED_AMOUNT', readableValue: t('sales.form.offer.type_fixed') || 'Fixed amount' },
          ]}
          onChange={(e) =>
            updateOffer({ discountType: e.target.value as SaleOffer['discountType'] })
          }
          isDisabled={isDisabled}
        />
        <InputShortText
          label={
            offer?.discountType === 'FIXED_AMOUNT'
              ? t('sales.form.offer.amount') || 'Amount'
              : t('sales.form.offer.percent') || 'Percentage'
          }
          defaultValue={offer?.discountValue != null ? String(offer.discountValue) : ''}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            updateOffer({ discountValue: Number.isNaN(parsed) ? 0 : parsed });
          }}
          isDisabled={isDisabled}
          placeholder={offer?.discountType === 'FIXED_AMOUNT' ? '0.00' : '10'}
        />
      </div>
      {isOfferPresent(offer) && (
        <p className="text-xs text-gray-600">
          {t('sales.form.offer.line_total', {
            total: lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            currency,
          }) || `Line total: ${lineTotal.toFixed(2)} ${currency}`}
        </p>
      )}
    </div>
  );
}

export default SaleOfferFields;
